import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled, { css } from 'styled-components'

import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import IconButton from 'LuxKit/Button/IconButton'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import ListItemThumbnail from 'LuxKit/List/ListItemThumbnail'
import NestableClickableListItem from 'LuxKit/List/NestableClickableListItem'
import productLabels from 'i18n/productLabels'
import type { HotelTilePrice } from '../hooks/useTripPlanHotelTileData'
import { PLACEHOLDER_TILE_BORDER_WIDTH } from './constants'
import TripPlanTileDashedOutline, { tripPlanTileOutlineHoverStyles } from './TripPlanTileDashedOutline'

const HOTEL_TILE_ICON_BACKGROUND = 'rgba(110, 79, 230, 0.2)'
const HOTEL_TILE_ICON_COLOUR = '#6E4FE6'

const HotelTileContainer = styled(NestableClickableListItem)`
  border: ${rem(PLACEHOLDER_TILE_BORDER_WIDTH)} solid transparent;
  padding: ${rem(12)} ${rem(14)};
  ${tripPlanTileOutlineHoverStyles}

  &&:not(:disabled):hover {
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  }
`

const HotelThumbnail = styled(ListItemThumbnail)`
  width: ${rem(40)};
  height: ${rem(40)};
  border-radius: ${rem(10)};

  &&& {
    background-color: ${HOTEL_TILE_ICON_BACKGROUND};
    color: ${HOTEL_TILE_ICON_COLOUR};
  }

  svg {
    width: ${rem(16)};
    height: ${rem(16)};
  }
`

const HotelTileActions = styled(Group)`
  flex-shrink: 0;
`

const browseButtonLayout = css`
  display: inline-flex;
  align-items: center;
  gap: ${rem(6)};
  white-space: nowrap;
  line-height: 1;
`

const HotelBrowseButton = styled(TextButton)`
  ${browseButtonLayout}
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.round};
  padding: ${rem(8)} ${rem(12)};

  &:hover,
  &:focus-visible {
    border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }
`

const BrowseHotelTile = styled(NestableClickableListItem)`
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.L};
  &.size-medium { padding: ${rem(12)}; }
  gap: ${rem(16)};
`

interface Props {
  hasAccommodation: boolean
  hotelTilePrice?: HotelTilePrice
  onOpen: () => void
}

function TripPlanHotelTile({
  hasAccommodation,
  hotelTilePrice,
  onOpen,
}: Props) {
  const intl = useIntl()

  const hotelsLabel = intl.formatMessage(productLabels.hotels)

  const searchHotelsLabel = intl.formatMessage({
    id: 'MIObslnviv0W_jIU8tqL',
    defaultMessage: 'Search hotels',
    description: 'Button label to open the hotel offers/search drawer from the trip planner stop details',
  })

  const hotelTilePriceLabel = hotelTilePrice === undefined ? undefined : intl.formatMessage(
    {
      id: 'BW9zGX4U8wuVNhip8PV5',
      defaultMessage: 'From {price}/night',
      description: 'Per-night hotel price copy shown on the trip planner stop hotel tile',
    },
    { price: <FormatCurrency value={hotelTilePrice.price} currency={hotelTilePrice.currency} format="roundedDollar" /> },
  )

  const hotelActionsLabel = intl.formatMessage({
    id: 'rJwViybkCxy7xhl9jleU',
    defaultMessage: 'Browse hotel offers',
    description: 'Visible heading on the hotel offers tile and accessible label for the button that opens hotel search',
  })

  if (hasAccommodation) {
    return (
      <BrowseHotelTile
        size="medium"
        title={hotelActionsLabel}
        subtitle={hotelTilePriceLabel}
        onClick={onOpen}
        startIcon={<HotelThumbnail kind="highlight-secondary" gutter="none"><LineBedIcon size="XS" /></HotelThumbnail>}
        endIcon={<LineAngleRightIcon size="S" />}
      />
    )
  }

  return (
    <HotelTileContainer
      onClick={onOpen}
      size="medium"
      title={hotelsLabel}
      titleWeight="bold"
      titleLineClamp={1}
      subtitle={hotelTilePriceLabel}
      subtitleLineClamp={1}
      startIcon={
        <HotelThumbnail kind="highlight-secondary" gutter="none">
          <LineBedIcon size="XS" />
        </HotelThumbnail>
      }
    >
      <TripPlanTileDashedOutline radius="S" />
      <HotelTileActions direction="horizontal" gap={8} verticalAlign="center">
        <HotelBrowseButton size="small" kind="tertiary" onClick={onOpen} startIcon={<LineSearchIcon size="XS" />}>
          {searchHotelsLabel}
        </HotelBrowseButton>
        <IconButton
          size="small"
          kind="tertiary"
          onClick={onOpen}
          aria-label={hotelActionsLabel}
        >
          <LineEllipsisVIcon />
        </IconButton>
      </HotelTileActions>
    </HotelTileContainer>
  )
}

export default TripPlanHotelTile
